import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast, Toaster } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Save, Copy, Mail, Printer, Download, Loader2,
  CheckCircle2, RotateCcw, Banknote, ExternalLink, Bell,
} from 'lucide-react';
import Seo from '@/components/Seo';
import InvoiceDocument from '@/components/InvoiceDocument';

const gbp = (n) => `£${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_LABEL = { draft: 'Draft', sent: 'Sent', part_paid: 'Part paid', paid: 'Paid', void: 'Void' };

export default function InvoiceEditorPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const pdfRef = useRef(null);
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [customPay, setCustomPay] = useState('');

  const business = useMemo(() => ({
    business_name: user?.business_name || user?.name,
    business_address: user?.business_address,
    business_phone: user?.business_phone,
    business_email: user?.business_email || user?.email,
    business_vat_number: user?.business_vat_number,
    logo_data: null,
    brand_color: user?.brand_color,
  }), [user]);
  const [logoData, setLogoData] = useState(null);

  useEffect(() => {
    api.get('/invoices/' + id).then(({ data }) => setInv(data)).catch(() => { toast.error('Invoice not found'); navigate('/invoices'); }).finally(() => setLoading(false));
    if (user?.has_logo) api.get('/me/logo').then(({ data }) => setLogoData(data.data_url)).catch(() => {});
  }, [id]);

  const setField = (k, v) => setInv((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setInv((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setInv((p) => ({ ...p, items: [...(p.items || []), { description: '', qty: 1, unit: '', unit_price: 0 }] }));
  const removeItem = (i) => setInv((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    const items = inv?.items || [];
    const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
    const vat = subtotal * ((parseFloat(inv?.vat_rate) || 0) / 100);
    const total = subtotal + vat;
    return { subtotal, vat, total, balance: total - (inv?.amount_paid || 0) };
  }, [inv]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: inv.title, customer_name: inv.customer_name, customer_address: inv.customer_address,
        customer_phone: inv.customer_phone, customer_email: inv.customer_email,
        items: inv.items, vat_rate: parseFloat(inv.vat_rate) || 0, notes: inv.notes || '',
        payment_details: inv.payment_details || '', deposit_amount: parseFloat(inv.deposit_amount) || 0,
        due_days: parseInt(inv.due_days) || 14, project_id: inv.project_id || null,
        reminders_enabled: inv.reminders_enabled !== false,
      };
      const { data } = await api.put('/invoices/' + id, payload);
      setInv(data);
      toast.success('Invoice saved');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const shareUrl = inv?.token ? `${window.location.origin}/i/${inv.token}` : '';
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success('Pay link copied'); };

  const sendEmail = async () => {
    if (!inv.customer_email) { toast.error('Add a customer email first (then Save).'); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      if (data.emailed) toast.success('Invoice emailed to customer');
      else toast.message(data.message || 'Email not configured — share the link instead');
      const { data: fresh } = await api.get('/invoices/' + id);
      setInv(fresh);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not send');
    } finally { setSending(false); }
  };

  const pay = async (action, amount) => {
    try {
      const { data } = await api.post(`/invoices/${id}/payment`, { action, amount, method: 'manual' });
      setInv(data);
      setCustomPay('');
      toast.success(action === 'unpaid' ? 'Marked unpaid' : 'Payment recorded');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not update payment');
    }
  };

  const remind = async () => {
    if (!inv.customer_email) { toast.error('Add a customer email first (then Save).'); return; }
    setReminding(true);
    try {
      const { data } = await api.post(`/invoices/${id}/remind`);
      if (data.emailed) toast.success('Reminder emailed to customer');
      else toast.message(data.message || 'Email not configured — share the link instead');
      const { data: fresh } = await api.get('/invoices/' + id);
      setInv(fresh);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not send reminder');
    } finally { setReminding(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete('/invoices/' + id); toast.success('Deleted'); navigate('/invoices'); }
    catch { toast.error('Delete failed'); }
  };

  const printPdf = () => window.print();
  const downloadPdf = async () => {
    const node = pdfRef.current?.querySelector('.print-page');
    if (!node) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const safe = (inv.invoice_number || 'invoice').toString().replace(/[^a-z0-9-_]+/gi, '-');
      await html2pdf().set({
        margin: [10, 8, 10, 8], filename: `${safe}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 1024 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(node).save();
      toast.success('PDF downloaded');
    } catch { toast.error('Could not generate PDF — try Print instead'); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16"><Loader2 className="animate-spin text-slate-900" size={40} /></div>;
  if (!inv) return null;

  const docForPrint = { ...inv, subtotal: totals.subtotal, vat_amount: totals.vat, total: totals.total, balance_due: totals.balance };
  const businessWithLogo = { ...business, logo_data: logoData };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title={`Invoice ${inv.invoice_number}`} noindex />
      <Toaster richColors position="top-right" />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 no-print">
        <Link to="/invoices" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand font-semibold"><ArrowLeft size={14} /> All invoices</Link>
        <div className="flex items-center gap-2">
          <span className="tm-chip tm-chip-orange font-mono" data-testid="editor-invoice-number">{inv.invoice_number}</span>
          <span className="tm-chip" data-testid="editor-status">{STATUS_LABEL[inv.status] || inv.status}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="tm-card p-4 mb-6 flex flex-wrap items-center gap-2 no-print">
        <button data-testid="invoice-save" onClick={save} disabled={saving} className="tm-btn text-sm">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
        <button data-testid="invoice-copy-link" onClick={copyLink} className="tm-btn tm-btn-ghost text-sm"><Copy size={15} /> Copy pay link</button>
        <a data-testid="invoice-open-link" href={shareUrl} target="_blank" rel="noreferrer" className="tm-btn tm-btn-ghost text-sm"><ExternalLink size={15} /> Preview</a>
        <button data-testid="invoice-send-email" onClick={sendEmail} disabled={sending} className="tm-btn tm-btn-ghost text-sm">{sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email to customer</button>
        <button data-testid="invoice-remind" onClick={remind} disabled={reminding} className="tm-btn tm-btn-ghost text-sm">{reminding ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} Send reminder</button>
        <button data-testid="invoice-download-pdf" onClick={downloadPdf} disabled={downloading} className="tm-btn tm-btn-ghost text-sm">{downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} PDF</button>
        <button data-testid="invoice-print" onClick={printPdf} className="tm-btn tm-btn-ghost text-sm"><Printer size={15} /> Print</button>
        <button data-testid="invoice-delete-editor" onClick={del} className="ml-auto text-rose-600 hover:text-rose-800 p-2" title="Delete"><Trash2 size={16} /></button>
      </div>

      {/* Payment tracking */}
      <div className="tm-card p-5 mb-6 no-print" data-testid="invoice-payment-panel">
        <div className="flex items-center gap-2 mb-3">
          <Banknote className="text-brand" size={20} />
          <h2 className="font-display font-bold text-lg uppercase tracking-tight">Payment tracking</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="border border-slate-200 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total</p><p className="font-mono font-semibold text-lg">{gbp(totals.total)}</p></div>
          <div className="border border-slate-200 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Paid</p><p className="font-mono font-semibold text-lg text-emerald-700" data-testid="invoice-paid-amount">{gbp(inv.amount_paid)}</p></div>
          <div className="border border-slate-200 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Balance due</p><p className="font-mono font-semibold text-lg text-brand" data-testid="invoice-balance">{gbp(totals.balance)}</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="pay-full" onClick={() => pay('full')} className="tm-btn text-sm"><CheckCircle2 size={15} /> Mark fully paid</button>
          {(parseFloat(inv.deposit_amount) || 0) > 0 && (
            <button data-testid="pay-deposit" onClick={() => pay('deposit')} className="tm-btn tm-btn-ghost text-sm">Record deposit ({gbp(inv.deposit_amount)})</button>
          )}
          <div className="flex items-center gap-1">
            <span className="font-mono text-xs">£</span>
            <input data-testid="pay-custom-amount" type="number" value={customPay} onChange={(e) => setCustomPay(e.target.value)} placeholder="Amount" className="tm-input w-28 py-2" />
            <button data-testid="pay-custom" onClick={() => pay('custom', parseFloat(customPay) || 0)} className="tm-btn tm-btn-ghost text-sm">Record</button>
          </div>
          {inv.amount_paid > 0 && (
            <button data-testid="pay-unpaid" onClick={() => pay('unpaid')} className="text-xs uppercase text-slate-500 hover:text-rose-600 inline-flex items-center gap-1 font-semibold"><RotateCcw size={13} /> Reset to unpaid</button>
          )}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input data-testid="inv-reminders-toggle" type="checkbox" checked={inv.reminders_enabled !== false} onChange={(e) => setField('reminders_enabled', e.target.checked)} className="w-4 h-4 accent-brand" />
            <span className="text-xs uppercase tracking-wider text-slate-700 font-semibold">Auto-reminders for this invoice</span>
          </label>
          {inv.reminder_count > 0 && (
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold" data-testid="inv-reminder-count">{inv.reminder_count} reminder{inv.reminder_count > 1 ? 's' : ''} sent</span>
          )}
        </div>
      </div>

      {/* Editor form */}
      <div className="grid lg:grid-cols-2 gap-6 no-print">
        <div className="tm-card p-6 space-y-4">
          <h2 className="font-display font-bold text-lg uppercase tracking-tight">Invoice details</h2>
          <div><label className="tm-label">Title / description</label><input data-testid="inv-title" value={inv.title || ''} onChange={(e) => setField('title', e.target.value)} className="tm-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tm-label">VAT rate (%)</label><input data-testid="inv-vat" type="number" value={inv.vat_rate} onChange={(e) => setField('vat_rate', e.target.value)} className="tm-input" /></div>
            <div><label className="tm-label">Due in (days)</label><input data-testid="inv-due" type="number" value={inv.due_days} onChange={(e) => setField('due_days', e.target.value)} className="tm-input" /></div>
          </div>
          <div><label className="tm-label">Deposit requested (£, optional)</label><input data-testid="inv-deposit" type="number" value={inv.deposit_amount} onChange={(e) => setField('deposit_amount', e.target.value)} className="tm-input" placeholder="0" /></div>
          <div><label className="tm-label">How to pay (bank details / instructions)</label><textarea data-testid="inv-payment-details" rows={3} value={inv.payment_details || ''} onChange={(e) => setField('payment_details', e.target.value)} className="tm-input" placeholder={'e.g. Bank transfer\nSort code 00-00-00\nAccount 12345678\nRef: invoice number'} /></div>
          <div><label className="tm-label">Notes</label><textarea data-testid="inv-notes" rows={2} value={inv.notes || ''} onChange={(e) => setField('notes', e.target.value)} className="tm-input" /></div>
        </div>

        <div className="tm-card p-6 space-y-4">
          <h2 className="font-display font-bold text-lg uppercase tracking-tight">Bill to</h2>
          <div><label className="tm-label">Customer name</label><input data-testid="inv-customer-name" value={inv.customer_name || ''} onChange={(e) => setField('customer_name', e.target.value)} className="tm-input" /></div>
          <div><label className="tm-label">Customer email</label><input data-testid="inv-customer-email" type="email" value={inv.customer_email || ''} onChange={(e) => setField('customer_email', e.target.value)} className="tm-input" placeholder="Needed to email the invoice" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tm-label">Phone</label><input data-testid="inv-customer-phone" value={inv.customer_phone || ''} onChange={(e) => setField('customer_phone', e.target.value)} className="tm-input" /></div>
            <div><label className="tm-label">Address</label><input data-testid="inv-customer-address" value={inv.customer_address || ''} onChange={(e) => setField('customer_address', e.target.value)} className="tm-input" /></div>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="tm-card p-6 mt-6 no-print">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg uppercase tracking-tight">Line items</h2>
          <button data-testid="inv-add-item" onClick={addItem} className="tm-btn tm-btn-ghost text-sm"><Plus size={15} /> Add line</button>
        </div>
        <div className="space-y-2">
          {(inv.items || []).map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-item-${i}`}>
              <input data-testid={`inv-item-desc-${i}`} value={it.description || ''} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="Description" className="tm-input col-span-5 py-2" />
              <input data-testid={`inv-item-qty-${i}`} type="number" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} placeholder="Qty" className="tm-input col-span-2 py-2" />
              <input data-testid={`inv-item-unit-${i}`} value={it.unit || ''} onChange={(e) => setItem(i, 'unit', e.target.value)} placeholder="Unit" className="tm-input col-span-2 py-2" />
              <input data-testid={`inv-item-price-${i}`} type="number" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} placeholder="£/unit" className="tm-input col-span-2 py-2" />
              <button onClick={() => removeItem(i)} className="col-span-1 text-slate-400 hover:text-rose-600 flex justify-center" title="Remove"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col items-end gap-1 text-sm">
          <div className="flex gap-8"><span className="text-slate-600">Subtotal</span><span className="font-mono font-bold w-28 text-right">{gbp(totals.subtotal)}</span></div>
          <div className="flex gap-8"><span className="text-slate-600">VAT ({inv.vat_rate}%)</span><span className="font-mono w-28 text-right">{gbp(totals.vat)}</span></div>
          <div className="flex gap-8 border-t border-slate-300 pt-1"><span className="font-display font-semibold uppercase">Total</span><span className="font-mono font-semibold text-lg text-brand w-28 text-right">{gbp(totals.total)}</span></div>
        </div>
      </div>

      {/* Hidden printable / PDF source */}
      <div ref={pdfRef}>
        <InvoiceDocument invoice={docForPrint} business={businessWithLogo} />
      </div>
    </div>
  );
}
